import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyIdProps,
  CompanyId,
  ErrorHandler,
} from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useEffect, useState } from "react";

type Props = ErrorHandler & Partial<BaseCompanyIdProps>;

interface ReturnProps {
  /** The groups of the company */
  groups: FaroCoreApiTypes.AdmCompanyGroup[];

  /** Indicator whether the workspace groups are currently being fetched */
  isFetchingWorkspaceGroups: boolean;
}

/**
 * Use this hook to fetch the groups for a specific companyId.
 * Returns the fetched groups and a loading flag.
 * If the loading fails, the fetched groups will be empty array.
 */
export function useGroupsOfWorkspace({
  companyId,
  errorHandler,
}: Props): ReturnProps {
  const [groups, setGroups] = useState<FaroCoreApiTypes.AdmCompanyGroup[]>([]);
  const [isFetchingWorkspaceGroups, setIsFetchingWorkspaceGroups] =
    useState<boolean>(true);

  // Fetches the groups
  useEffect(() => {
    async function fetchGroups(companyId: CompanyId): Promise<void> {
      setIsFetchingWorkspaceGroups(true);
      try {
        const fetchedGroups =
          await faroCoreApiClient.admin.getCompanyGroupsById({ companyId });

        // Sort groups by name (case-insensitive)
        fetchedGroups.sort((groupA, groupB) =>
          groupA.name.localeCompare(groupB.name, undefined, {
            sensitivity: "base",
          })
        );
        setGroups(fetchedGroups);
      } catch (error) {
        errorHandler({
          id: `use-groups-of-company-${Date.now().toString()}`,
          title: "Could not load groups",
          error,
        });
        setGroups([]);
      } finally {
        setIsFetchingWorkspaceGroups(false);
      }
    }

    if (companyId) {
      void fetchGroups(companyId);
    }
  }, [companyId, errorHandler]);

  if (!companyId) {
    return { groups: [], isFetchingWorkspaceGroups: false };
  }

  return { groups, isFetchingWorkspaceGroups };
}
